import { useState } from "react";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAppContext } from "../context";
import { api } from "../api";
import {
  Star,
  Loader2,
  ChevronDown,
  AlertTriangle,
  Info,
  ExternalLink,
} from "lucide-react";
import { cn } from "../lib/utils";

interface TierInfo {
  id: number;
  name: string;
  color: string;
}

interface WinEvent {
  id: number;
  winnerId: number;
  prizeId: number;
  gachaMachineId: number;
  weightedInsuredValue: number;
  tierId: number | null;
  gapFromPrevious: number | null;
  pullNumber: number | null;
  timestamp: Date;
  prizeName: string;
  prizeImage: string | null;
  insuredValue: number;
  certId: string | null;
  gradingCompany: string | null;
  grade: string | null;
  winnerWallet: string;
  tierName: string | null;
  tierColor: string | null;
}

interface EventsResponse {
  data: WinEvent[];
  pagination: {
    hasMore: boolean;
    nextCursor: string | null;
    limit: number;
  };
}

function shortenWallet(address: string): string {
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
}

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(date).toLocaleDateString();
}

export function LegendariesPage() {
  const { selectedMachine, timeRange } = useAppContext();
  const [selectedTierId, setSelectedTierId] = useState<number | "all">("all");

  // Fetch machine info to get tiers
  const { data: machineData, isLoading: isLoadingMachine } = useQuery({
    queryKey: ["machine-stats", selectedMachine],
    queryFn: async () => {
      if (!selectedMachine) return null;
      const response = await api.stats
        .machines({ identifier: selectedMachine })
        .get();
      if (response.data && "machine" in response.data) {
        return response.data as {
          machine: { tiersConfigured: boolean };
          tiers: TierInfo[];
        };
      }
      throw new Error("Failed to fetch machine info");
    },
    enabled: !!selectedMachine,
    staleTime: 60000,
  });

  // Fetch tiered events with pagination
  const {
    data: eventsData,
    isLoading: isLoadingEvents,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: ["tiered-events", selectedMachine, timeRange, selectedTierId],
    queryFn: async ({ pageParam }) => {
      if (!selectedMachine) return null;
      const query: Record<string, string> = {
        tieredOnly: "true",
        limit: "20",
      };
      if (timeRange !== "all") {
        query.range = timeRange;
        query.tzOffset = String(new Date().getTimezoneOffset());
      }
      if (pageParam) query.cursor = pageParam;
      if (selectedTierId !== "all") query.tierId = String(selectedTierId);

      const response = await api.stats
        .machines({ identifier: selectedMachine })
        .events.get({ query });

      if (response.data && "data" in response.data) {
        return response.data as EventsResponse;
      }
      throw new Error("Failed to fetch events");
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage?.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    enabled: !!selectedMachine,
    staleTime: 30000,
  });

  const allEvents = eventsData?.pages.flatMap((p) => p?.data ?? []) ?? [];
  const tiers = machineData?.tiers ?? [];
  const isLoading = isLoadingMachine || isLoadingEvents;

  // No machine selected
  if (!selectedMachine) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Card className="max-w-md">
          <CardContent className="py-8 text-center">
            <Info className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-lg font-semibold mb-2">No Machine Selected</h2>
            <p className="text-muted-foreground">
              Select a machine from the dropdown to view tier wins.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Card className="max-w-md border-destructive/50">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
            <h2 className="text-lg font-semibold mb-2">Failed to Load Data</h2>
            <p className="text-muted-foreground">
              {error instanceof Error ? error.message : "An unexpected error occurred"}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <Star className="h-6 w-6 text-yellow-400" />
              </div>
              <div>
                <CardTitle>Tier Wins</CardTitle>
                <CardDescription>
                  Recent prize wins classified into tiers
                </CardDescription>
              </div>
            </div>

            {/* Tier Filter */}
            {tiers.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedTierId("all")}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-sm font-medium transition-colors border",
                    selectedTierId === "all"
                      ? "bg-primary text-primary-foreground border-transparent"
                      : "border-border text-muted-foreground hover:text-foreground"
                  )}
                >
                  All Tiers
                </button>
                {tiers.map((tier) => (
                  <button
                    key={tier.id}
                    onClick={() => setSelectedTierId(tier.id)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-sm font-medium transition-colors border",
                      selectedTierId === tier.id
                        ? "border-transparent text-white"
                        : "border-border text-muted-foreground hover:text-foreground"
                    )}
                    style={
                      selectedTierId === tier.id
                        ? { backgroundColor: tier.color }
                        : undefined
                    }
                  >
                    {tier.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && allEvents.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : allEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-4 bg-secondary rounded-full mb-4">
                <Star className="h-12 w-12 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Tier Wins Yet</h3>
              <p className="text-muted-foreground max-w-md">
                {machineData?.machine.tiersConfigured
                  ? "No tier wins have been recorded for this machine in the selected time range."
                  : "Tiers haven't been configured for this machine yet."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {allEvents.map((event) => (
                <div
                  key={event.id}
                  className="p-4 rounded-lg border border-border bg-card/50 hover:bg-card transition-colors"
                  style={{
                    borderLeftColor: event.tierColor ?? undefined,
                    borderLeftWidth: event.tierColor ? 4 : undefined,
                  }}
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* Prize Image */}
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-secondary shrink-0 overflow-hidden">
                      {event.prizeImage ? (
                        <img
                          src={event.prizeImage}
                          alt={event.prizeName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Star className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Prize Info */}
                    <div className="flex-1 min-w-0">
                      {/* Title row */}
                      <div className="flex items-start sm:items-center gap-2 mb-1">
                        <h4 className="font-semibold text-sm sm:text-base line-clamp-2 sm:truncate sm:line-clamp-none">
                          {event.prizeName}
                        </h4>
                        {event.tierName && (
                          <Badge
                            variant="outline"
                            className="shrink-0 text-xs"
                            style={{
                              borderColor: event.tierColor ?? undefined,
                              color: event.tierColor ?? undefined,
                            }}
                          >
                            {event.tierName}
                          </Badge>
                        )}
                      </div>
                      {/* Stats row */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-muted-foreground">
                        <span className="font-medium">${event.insuredValue.toLocaleString()}</span>
                        {event.gradingCompany && event.grade && (
                          <span className="hidden sm:inline">
                            {event.gradingCompany} {event.grade}
                          </span>
                        )}
                        {event.gapFromPrevious !== null && (
                          <span>Gap: {event.gapFromPrevious}</span>
                        )}
                        {/* Mobile: show wallet + time inline */}
                        <span className="sm:hidden">
                          <a
                            href={`https://solscan.io/account/${event.winnerWallet}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono hover:text-foreground"
                          >
                            {shortenWallet(event.winnerWallet)}
                          </a>
                          {" · "}
                          {formatTimeAgo(event.timestamp)}
                        </span>
                      </div>
                    </div>

                    {/* Winner & Time - Desktop only */}
                    <div className="hidden sm:block text-right text-sm shrink-0">
                      <a
                        href={`https://solscan.io/account/${event.winnerWallet}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-muted-foreground hover:text-foreground flex items-center gap-1 justify-end"
                      >
                        {shortenWallet(event.winnerWallet)}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      <p className="text-muted-foreground mt-1">
                        {formatTimeAgo(event.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Load More */}
              {hasNextPage && (
                <div className="flex justify-center pt-4">
                  <Button
                    variant="outline"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-2" />
                        Load More
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
